"use client";

import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import { Lightbulb } from "lucide-react";
import { Button } from "@/components/shared/ui/button";
import { cn } from "@/lib/utils/shared/core-utils";

/**
 * @file ThemeToggleBulb.tsx
 * @description 전구 메타포를 사용한 테마 전환 스위치 컴포넌트
 *
 * 주요 기능:
 * 1. Light Mode: 켜진 전구 (amber 색상, 글로우 효과)
 * 2. Dark Mode: 꺼진 전구 (회색 라인)
 * 3. 부드러운 애니메이션 전환
 * 4. Next.js Hydration Mismatch 방지
 *
 * @dependencies
 * - next-themes: 테마 관리
 * - lucide-react: Lightbulb 아이콘
 * - @/components/shared/ui/button: 버튼 컴포넌트
 */

export function ThemeToggleBulb() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // 클라이언트에서만 테마를 읽도록 함 (hydration mismatch 방지)
  useEffect(() => {
    setMounted(true);
  }, []);

  // mounted 전에는 항상 light 모드로 표시 (서버와 클라이언트 일치)
  const isDark = mounted && resolvedTheme === "dark";

  const toggleTheme = () => {
    setTheme(isDark ? "light" : "dark");
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      onClick={toggleTheme}
      aria-label={isDark ? "라이트 모드로 전환" : "다크 모드로 전환"}
      className={cn(
        "relative w-10 h-10 rounded-full transition-all duration-300",
        "hover:scale-110 active:scale-95",
        // Light Mode 스타일
        !isDark
          ? "bg-white border-amber-200 hover:bg-amber-50"
          : // Dark Mode 스타일
            "bg-zinc-800 border-zinc-700 hover:bg-zinc-700"
      )}
    >
      <Lightbulb
        className={cn(
          "h-5 w-5 transition-all duration-300",
          // Light Mode: 켜진 전구 (채워진 노란색 + 글로우)
          !isDark
            ? "fill-amber-500 text-amber-500 drop-shadow-[0_0_8px_rgba(245,158,11,0.6)]"
            : // Dark Mode: 꺼진 전구 (회색 라인)
              "fill-none text-zinc-400"
        )}
      />
    </Button>
  );
}

